import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { crud } from './crudService';

// Service instances for each collection - matching your existing Firebase structure
export const TypeService = crud<BusinessType>('types', { orderByField: 'name' }); // Using 'types' not 'businessTypes'
export const UnitService = crud<Unit>('units', { orderByField: 'name' });
export const CostCenterService = crud<CostCenter>('costCenters', { orderByField: 'name' });
export const CategoryService = crud<ExpenseCategory>('categories', { orderByField: 'name' }); // Using 'categories' not 'expenseCategories'
export const ItemService = crud<Item>('items', { orderByField: 'name' });
export const ShopService = crud<Shop>('shops', { orderByField: 'name' });
export const VendorService = crud<Vendor>('vendors', { orderByField: 'name' });
export const ConfigFlagService = crud<ConfigFlag>('configFlags', { orderByField: 'key' });
export const ModuleService = crud<Module>('modules', { orderByField: 'name' });
export const ExpenseNatureService = crud<ExpenseNature>('expenseNatures', { orderByField: 'name' });

// Type definitions for each entity
export interface BusinessType {
  id?: string;
  name: string;
  description?: string;
  code?: string; // Business type code
  active?: boolean; // Active status
  allowsUnits?: boolean; // Whether this type supports units
  allowsCostCenters?: boolean; // Whether this type supports cost centers
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface Unit {
  id?: string;
  name: string;
  abbreviation?: string;
  description?: string;
  code?: string; // Unit code
  type: string; // Link to business type (matches your Firebase field name)
  businessType?: string; // Business type name for reference
  location?: string; // Physical location
  active?: boolean; // Active status
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface CostCenter {
  id?: string;
  name: string;
  type: string; // References business type ID
  code?: string; // Cost center code
  businessType?: string; // Business type name for reference
  description?: string;
  active?: boolean; // Database uses 'active' field
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface ExpenseCategory {
  id?: string;
  name: string;
  costCenterId?: string; // References cost center ID
  type?: string; // References business type ID (database field)
  nature?: string; // References expense nature code (e.g., 'routine', 'stock', 'marketing', etc.)
  supportsItems?: boolean; // NEW - enables item dropdown
  requiresNameField?: boolean; // Database field
  supportsUnits?: boolean; // Database field - only shown if supportsItems = false
  unitLabel?: string; // Database field

  // NEW: Mixed stock usage configuration
  usageMode?: 'individual' | 'combined' | 'both'; // Stock usage tracking mode
  combinedUsageLabel?: string; // Label for combined usage (e.g., "Mixed Firewood Usage")
  defaultDeductionStrategy?: 'proportional' | 'fifo' | 'equal' | 'custom'; // Default deduction strategy for combined usage

  active?: boolean; // Database field
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface Item {
  id?: string;
  name: string;
  categoryId: string;
  unitId: string;
  // Optional denormalized references for faster lookups and filtering
  // Will be auto-populated from the selected category when creating/editing items
  costCenterId?: string; // Derived from category.costCenterId
  typeId?: string; // Derived from category.type (business type id)
  description?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Shop {
  id?: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Vendor {
  id?: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  gstNumber?: string;
  contactPerson?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export type ConfigFlagValueType = 'string' | 'number' | 'boolean' | 'url' | 'json';
export type ConfigFlagEnvironment = 'all' | 'development' | 'staging' | 'production';

export interface ConfigFlagAuditEntry {
  timestamp: Date;
  userId?: string;
  userName?: string;
  action: 'created' | 'updated' | 'deleted' | 'toggled';
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
}

export interface ConfigFlag {
  id?: string;
  key: string;
  value: string;
  valueType?: ConfigFlagValueType; // Type of the value for validation
  environment?: ConfigFlagEnvironment; // Which environment(s) this flag applies to
  description?: string;
  isActive?: boolean;
  auditLog?: ConfigFlagAuditEntry[]; // Track all changes
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export interface Module {
  id?: string;
  name: string;
  description?: string;
  route?: string;
  icon?: string;
  isActive?: boolean;
  inUse?: boolean; // Indicates if this module is currently being used
  orderIndex?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ExpenseNature {
  id?: string;
  name: string;
  description?: string;
  code: string; // Unique code like 'routine', 'stock', 'payroll', etc.
  color?: string; // Tailwind color classes for display
  icon?: string; // Optional icon for the nature
  isActive?: boolean;
  isSystemDefined?: boolean; // True for built-in natures, false for custom ones
  orderIndex?: number;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

export type ProductionSettings = { nonAdminEditWindowDays: number };

export async function getProductionSettings(): Promise<ProductionSettings> {
  const ref = doc(db, 'settings', 'production');
  const snap = await getDoc(ref);
  const data = (snap.exists() ? snap.data() : {}) as Partial<ProductionSettings>;
  return { nonAdminEditWindowDays: data.nonAdminEditWindowDays ?? 7 };
}
